import { Box, Paper, Typography } from '@mui/material'
import type { UnusedMediaReportSummary } from '@api/types/storageReport'
import { formatBytes } from './storageFormat'

interface Tile {
  label: string
  count: number
  bytes?: number
  caption: string
  tone?: 'warning' | 'error'
}

/** The report's totals, one tile per kind of finding. */
export function ReportSummary({ summary }: { summary: UnusedMediaReportSummary }) {
  const tiles: Tile[] = [
    {
      label: 'Unused files',
      count: summary.unusedFiles,
      bytes: summary.unusedBytes,
      caption: 'Files outside anyone’s library that nothing references',
      tone: summary.unusedFiles > 0 ? 'warning' : undefined,
    },
    {
      label: 'Kept in libraries',
      count: summary.libraryOnlyFiles,
      bytes: summary.libraryOnlyBytes,
      caption: 'In people’s own libraries but used nowhere — kept on purpose, not orphans',
    },
    {
      label: 'Untracked in bucket',
      count: summary.untrackedObjects,
      bytes: summary.untrackedBytes,
      caption: summary.bucketScanned ? 'Objects with no media record' : 'Bucket could not be listed',
      tone: summary.untrackedObjects > 0 ? 'warning' : undefined,
    },
    {
      label: 'Missing from bucket',
      count: summary.missingObjects,
      caption: 'Media records whose file is gone — broken images',
      tone: summary.missingObjects > 0 ? 'error' : undefined,
    },
    {
      label: 'Waiting to be purged',
      count: summary.softDeletedFiles,
      bytes: summary.softDeletedBytes,
      caption: 'Soft-deleted; removed 30 days after deletion',
    },
    {
      label: 'Bucket total',
      count: summary.bucketObjects,
      bytes: summary.bucketBytes,
      caption: `${summary.activeFiles.toLocaleString()} active media records (${formatBytes(summary.activeBytes)})`,
    },
  ]

  return (
    <Box
      sx={{
        display: 'grid',
        // Two per row even on a phone: six full-width tiles pushed the tabs two screens down.
        gap: { xs: 1, sm: 2 },
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
        mb: { xs: 2, sm: 3 },
      }}
    >
      {tiles.map((tile) => (
        <Paper
          key={tile.label}
          variant="outlined"
          sx={{
            p: { xs: 1.25, sm: 2 },
            minWidth: 0,
            borderLeft: 4,
            borderLeftColor: tile.tone ? `${tile.tone}.main` : 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: undefined } }}>
            {tile.label}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: { xs: '1.15rem', sm: undefined } }}
          >
            {tile.count.toLocaleString()}
            {tile.bytes != null && (
              // Wraps under the count on a narrow tile rather than overflowing it.
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1, display: 'inline-block', fontSize: { xs: '0.8rem', sm: undefined } }}>
                {formatBytes(tile.bytes)}
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
            {tile.caption}
          </Typography>
        </Paper>
      ))}
    </Box>
  )
}
